import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { daaraService, Daara as DaaraType } from '../services/daaraService';
import { PaginationControls } from '../components/PaginationControls';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

const JOURS = [
  { value: '', label: 'Tous les jours' },
  { value: 'lundi', label: 'Lundi' },
  { value: 'mardi', label: 'Mardi' },
  { value: 'mercredi', label: 'Mercredi' },
  { value: 'jeudi', label: 'Jeudi' },
  { value: 'vendredi', label: 'Vendredi' },
  { value: 'samedi', label: 'Samedi' },
  { value: 'dimanche', label: 'Dimanche' },
];

export const Daara: React.FC = () => {
  const { user } = useAuth();
  const [daaras, setDaaras] = useState<DaaraType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    jour: '',
    search: '',
    section: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchDaaras();
  }, [filters, currentPage]);

  const fetchDaaras = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: 12,
      };

      if (filters.jour) {
        params.jour = filters.jour;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.section) {
        params.section = filters.section;
      }

      const response = await daaraService.getAll(params);
      
      // Gérer la structure de réponse paginée
      if (response && typeof response === 'object') {
        const results = response.results || (Array.isArray(response) ? response : []);
        const count = response.count || results.length;
        
        setDaaras(results);
        setTotalCount(count);
        setTotalPages(Math.max(1, Math.ceil(count / 12)));
      } else {
        setDaaras([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setDaaras([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (daaraId: number) => {
    if (!user) {
      alert('Veuillez vous connecter pour aimer un cours');
      return;
    }

    try {
      await daaraService.like(daaraId);
      fetchDaaras();
    } catch (error: any) {
      console.error('Erreur:', error);
      if (error.response?.status === 401) {
        alert('Veuillez vous connecter pour aimer un cours');
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-50/50 py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-4 sm:mb-6">
            <div className="relative inline-block bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-3xl px-8 py-8 shadow-2xl overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <div className="relative z-10">
                <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg">
                  Daara
                </h1>
                <p className="text-green-50 text-xl font-medium">
                  Découvrez tous les cours du Daara organisés par jour de la semaine
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filtres mobiles */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
          <div className="grid grid-cols-1 gap-3">
            <select
              value={filters.section}
              onChange={(e) => {
                setFilters({ ...filters, section: e.target.value });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
            >
              <option value="">Toutes les sections</option>
              <option value="VCN">🎓 VCN (Université)</option>
              <option value="HDR">🏛️ HDR</option>
              <option value="SANTE">🩺 SANTÉ</option>
            </select>
            
            <select
              value={filters.jour}
              onChange={(e) => {
                setFilters({ ...filters, jour: e.target.value });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
            >
              {JOURS.map(jour => (
                <option key={jour.value} value={jour.value}>{jour.label}</option>
              ))}
            </select>
            
            <input
              type="text"
              placeholder="🔍 Rechercher..."
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
            />
          </div>
        </div>
      </div>

      {/* Liste des Daaras */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* Sidebar filtres - desktop */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50 sticky top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Filtres</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
                    <select
                      value={filters.section}
                      onChange={(e) => {
                        setFilters({ ...filters, section: e.target.value });
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    >
                      <option value="">Toutes les sections</option>
                      <option value="VCN">🎓 VCN (Université)</option>
                      <option value="HDR">🏛️ HDR</option>
                      <option value="SANTE">🩺 SANTÉ</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Jour de la semaine</label>
                    <select
                      value={filters.jour}
                      onChange={(e) => {
                        setFilters({ ...filters, jour: e.target.value });
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    >
                      {JOURS.map(jour => (
                        <option key={jour.value} value={jour.value}>{jour.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
                    <input
                      type="text"
                      placeholder="🔍 Rechercher..."
                      value={filters.search}
                      onChange={(e) => {
                        setFilters({ ...filters, search: e.target.value });
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex justify-center items-center py-32">
                  <div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div>
                </div>
              ) : daaras.length > 0 ? (
                <>
                  <div className="mb-6 text-gray-600">
                    {totalCount} cours trouvé{totalCount > 1 ? 's' : ''}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {daaras.map((daara) => (
                      <Link
                        key={daara.id}
                        to={`/daara/${daara.url_slug || daara.id}`}
                        className="group animate-slide-up bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-green-100/50"
                      >
                        {/* Photo principale */}
                        <div className="relative h-48 bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
                          {daara.photo_principale ? (
                            <img
                              src={daara.photo_principale}
                              alt={daara.titre}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-6xl opacity-50 text-white">📚</span>
                            </div>
                          )}
                          <div className="absolute top-4 left-4 flex flex-col gap-2">
                            <span className="px-3 py-1 bg-green-600/90 backdrop-blur-sm rounded-full text-xs font-bold text-white">
                              {JOURS.find(j => j.value === daara.jour)?.label || daara.jour}
                            </span>
                            {daara.section && (
                              <span className="px-3 py-1 bg-blue-600/90 backdrop-blur-sm rounded-full text-xs font-bold text-white">
                                {daara.section === 'VCN' ? '🎓 VCN' : daara.section === 'HDR' ? '🏛️ HDR' : '🩺 SANTÉ'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Contenu */}
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors line-clamp-2">
                            {daara.titre}
                          </h3>
                          
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                            {daara.description}
                          </p>

                          {/* Stats */}
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4 pb-4 border-b border-gray-100">
                            <span className="flex items-center space-x-1">
                              <span>👁️</span>
                              <span>{daara.nombre_vues}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <span>❤️</span>
                              <span>{daara.nombre_likes}</span>
                            </span>
                            {daara.nombre_commentaires !== undefined && (
                              <span className="flex items-center space-x-1">
                                <span>💬</span>
                                <span>{daara.nombre_commentaires}</span>
                              </span>
                            )}
                          </div>

                          <span className="text-green-700 font-bold text-sm group-hover:translate-x-2 transition-transform inline-flex items-center space-x-1">
                            <span>Voir détails</span>
                            <span>→</span>
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8">
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-32">
                  <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-green-100/50">
                    <div className="text-8xl mb-6">📚</div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun cours trouvé</h3>
                    <p className="text-gray-600 text-lg">
                      Essayez de modifier vos filtres pour voir plus de résultats
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

